import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wrench, LogOut, MessageSquare, Settings as SettingsIcon, Crown, Gift } from "lucide-react";
import { BrandWord } from "@/components/BrandWord";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  // Light-weight unread badge — polled every 60s while the navbar is mounted.
  // Cheap query (just a count_documents on indexed fields) and gives an
  // always-visible signal that something needs attention.
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/messages/unread-count");
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch { /* silent — badge just stays as-is */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60_000);
    // Also refresh when the user returns to the tab/app.
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const isOwner = user?.role === "owner";
  const isShop = user?.role === "shop";
  const tier = user?.subscription_tier || "free";
  const isPremium = tier === "premium";
  const isPro = tier === "pro";
  const isPaid = isPremium || isPro;
  const trialUntil = user?.welcome_trial_until;
  const premiumUntil = user?.premium_until;
  const isOnTrial = !!(isShop && isPaid && trialUntil && premiumUntil && new Date(premiumUntil).getTime() <= new Date(trialUntil).getTime() + 60_000);
  const trialDaysLeft = isOnTrial && trialUntil ? Math.max(0, Math.ceil((new Date(trialUntil).getTime() - Date.now()) / 86_400_000)) : 0;

  return (
    <header
      className="sticky top-0 z-50 bg-[rgba(9,9,11,0.8)] backdrop-blur-xl border-b border-white/10 safe-top"
      data-testid="top-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
        <Link to={user ? (user.role === "admin" ? "/admin" : isShop ? "/shop" : "/owner") : "/"} className="flex items-center gap-2.5" data-testid="nav-logo">
          <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center">
            <Wrench className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <BrandWord className="font-heading text-lg font-black tracking-tight" />
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-sm">
          {isOwner && (
            <>
              <Link to="/owner" className={`btn-ghost ${location.pathname === "/owner" ? "text-white" : ""}`} data-testid="nav-owner-home">Dashboard</Link>
              <Link to="/post" className={`btn-ghost ${location.pathname === "/post" ? "text-white" : ""}`} data-testid="nav-post-issue">Post Issue</Link>
              <Link to="/shops" className="btn-ghost" data-testid="nav-shops">Shops</Link>
              <Link to="/map" className={`btn-ghost ${location.pathname === "/map" ? "text-white" : ""}`} data-testid="nav-map">Map</Link>
              <Link to="/messages" className="btn-ghost" data-testid="nav-messages">Messages</Link>
            </>
          )}
          {isShop && (
            <>
              <Link to="/shop" className="btn-ghost" data-testid="nav-shop-home">Dashboard</Link>
              <Link to="/browse" className="btn-ghost" data-testid="nav-browse">Browse Jobs</Link>
              <Link to="/map" className={`btn-ghost ${location.pathname === "/map" ? "text-white" : ""}`} data-testid="nav-map">Map</Link>
              <Link to="/messages" className="btn-ghost" data-testid="nav-messages">Messages</Link>
              <Link to="/shop-profile" className="btn-ghost" data-testid="nav-shop-profile">Profile</Link>
            </>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" className="btn-ghost" data-testid="nav-admin">Admin Console</Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              {isShop && isOnTrial && (
                <Link
                  to="/upgrade"
                  className="inline-flex items-center gap-1.5 border border-[#FF9500]/40 bg-[#FF9500]/10 hover:bg-[#FF9500]/20 text-[#FF9500] text-[10px] uppercase tracking-widest px-2.5 py-1 transition-colors"
                  title={`Free trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left. Click to keep Premium.`}
                  data-testid="nav-trial-badge"
                >
                  <Gift className="w-3 h-3" />
                  <span className="hidden xs:inline">Trial · </span>{trialDaysLeft}d
                </Link>
              )}
              {isShop && isPremium && !isOnTrial && (
                <Link
                  to="/upgrade"
                  className="inline-flex items-center gap-1.5 border border-[#FF3B30]/40 bg-[#FF3B30]/5 hover:bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] uppercase tracking-widest px-2.5 py-1 transition-colors"
                  title="Premium active — tap to manage"
                  data-testid="nav-premium-badge"
                >
                  <Crown className="w-3 h-3" />
                  Premium
                </Link>
              )}
              {isShop && isPro && !isOnTrial && (
                <Link
                  to="/upgrade"
                  className="inline-flex items-center gap-1.5 border border-[#FF9500]/40 bg-[#FF9500]/5 hover:bg-[#FF9500]/10 text-[#FF9500] text-[10px] uppercase tracking-widest px-2.5 py-1 transition-colors"
                  title="Pro active — tap to manage"
                  data-testid="nav-pro-badge"
                >
                  <Crown className="w-3 h-3" />
                  Pro
                </Link>
              )}
              {isShop && !isPaid && (
                <Link
                  to="/upgrade"
                  className="inline-flex items-center gap-1.5 border border-[#FF3B30]/60 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white text-[10px] uppercase tracking-widest px-2.5 py-1 transition-colors font-semibold"
                  title="Upgrade your shop plan"
                  data-testid="nav-upgrade-link"
                >
                  <Crown className="w-3 h-3" />
                  Upgrade
                </Link>
              )}
              <span className="text-xs uppercase tracking-widest text-zinc-500 hidden sm:inline">
                {user.role === "admin" ? "ADMIN" : isShop ? user.shop_name || user.name : user.anonymous_handle}
              </span>
              <Link
                to="/messages"
                className="btn-ghost p-2 relative"
                title="Messages"
                data-testid="nav-messages-icon"
              >
                <MessageSquare className="w-4 h-4" />
                {unread > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-[#FF3B30] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
                    data-testid="nav-messages-unread-badge"
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Link>
              <Link to="/settings" className="btn-ghost p-2" title="Settings" data-testid="nav-settings">
                <SettingsIcon className="w-4 h-4" />
              </Link>
              <button onClick={onLogout} className="btn-ghost flex items-center gap-2" data-testid="logout-btn" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="nav-login">Login</Link>
              <Link to="/login?mode=register" className="btn-primary" data-testid="nav-signup">Sign Up</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
